import { FileStat, IFileSystem, SearchOptions, ReadFileOptions, ReadFileResult, FileIconResult, FolderSizeResult, ChildrenSizesResult } from './IFileSystem';
import { useFileSearchStatusStore } from '../store/useFileSearchStatus';
import { FileSearchStatus } from '../../../types';

// JSON-RPC 2.0 Types
interface JsonRpcRequest {
  jsonrpc: '2.0';
  method: string;
  params: any;
  id: number;
}

interface JsonRpcResponse {
  jsonrpc: '2.0';
  result?: any;
  error?: {
    code: number;
    message: string;
    data?: any;
  };
  id: number;
}

// Streaming response handler type
type StreamHandler = {
  onBatch: (files: FileStat[], total: number, offset: number, hasMore: boolean) => void;
  onComplete: () => void;
  onError: (err: Error) => void;
};

export class WebRTCSystem implements IFileSystem {
  private dataChannel: RTCDataChannel | null = null;
  private pendingRequests = new Map<number, { resolve: (val: any) => void; reject: (err: any) => void }>();
  private streamingRequests = new Map<number, StreamHandler>(); // For streaming responses
  private nextId = 1;
  private statusPollingTimer: ReturnType<typeof setInterval> | null = null;

  // Buffer thresholds for flow control
  private static readonly BUFFER_LOW_THRESHOLD = 64 * 1024;   // 64KB - resume sending
  private static readonly BUFFER_HIGH_THRESHOLD = 512 * 1024; // 512KB - pause sending

  constructor(dataChannel?: RTCDataChannel) {
    if (dataChannel) {
      this.setDataChannel(dataChannel);
    }
  }

  /** Check if the DataChannel is connected and ready */
  get isConnected(): boolean {
    return this.dataChannel !== null && this.dataChannel.readyState === 'open';
  }

  /** Get the underlying DataChannel for direct binary transfer */
  get channel(): RTCDataChannel | null {
    return this.dataChannel;
  }

  /** Wait for buffer to drain using bufferedamountlow event (more efficient than polling) */
  private waitForBufferDrain(): Promise<void> {
    const dc = this.dataChannel;
    if (!dc || dc.readyState !== 'open') {
      return Promise.resolve();
    }

    if (dc.bufferedAmount < WebRTCSystem.BUFFER_HIGH_THRESHOLD) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      // Use native bufferedamountlow event for efficient waiting
      dc.bufferedAmountLowThreshold = WebRTCSystem.BUFFER_LOW_THRESHOLD;

      const onLow = () => {
        dc.removeEventListener('bufferedamountlow', onLow);
        resolve();
      };

      dc.addEventListener('bufferedamountlow', onLow);

      // Timeout fallback (10 seconds)
      setTimeout(() => {
        dc.removeEventListener('bufferedamountlow', onLow);
        resolve();
      }, 10000);
    });
  }

  /**
   * Send binary data directly with flow control
   * Returns a promise that resolves when the data is queued (not necessarily sent)
   */
  async sendBinary(data: ArrayBuffer): Promise<void> {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      throw new Error('DataChannel not open');
    }

    await this.waitForBufferDrain();

    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      throw new Error('DataChannel closed while waiting');
    }

    this.dataChannel.send(data);
  }

  public setDataChannel(dc: RTCDataChannel) {
    this.dataChannel = dc;

    // fs-rpc channel is dedicated to file manager JSON-RPC only
    // No need to preserve existing handlers since this is a dedicated channel
    this.dataChannel.onmessage = (event: MessageEvent) => {
      this.handleMessage(event);
    };
  }

  /**
   * Handle incoming message, returns true if it was a JSON-RPC response
   */
  private handleMessage(event: MessageEvent): boolean {
    try {
      let text: string;
      const d = event.data;
      if (typeof d === 'string') {
        text = d;
      } else if (d?.byteLength !== undefined) {
        text = new TextDecoder().decode(d);
      } else {
        return false;
      }

      const response: JsonRpcResponse = JSON.parse(text);

      if (response.jsonrpc !== '2.0' || !response.id) {
        return false;
      }

      // Check if this is a streaming response
      if (this.streamingRequests.has(response.id)) {
        const handler = this.streamingRequests.get(response.id)!;

        if (response.error) {
          this.streamingRequests.delete(response.id);
          handler.onError(new Error(`[WebRTC] ${response.error.message}`));
        } else if (response.result) {
          const { files, total, offset, hasMore } = response.result;
          handler.onBatch(files || [], total || 0, offset || 0, hasMore ?? false);

          // If no more data, complete the stream
          if (!hasMore) {
            this.streamingRequests.delete(response.id);
            handler.onComplete();
          }
        }
        return true;
      }

      // Regular (non-streaming) response
      if (this.pendingRequests.has(response.id)) {
        const { resolve, reject } = this.pendingRequests.get(response.id)!;
        this.pendingRequests.delete(response.id);

        if (response.error) {
          reject(new Error(`[WebRTC] ${response.error.message} (${response.error.code})`));
        } else {
          resolve(response.result);
        }
        return true;
      }
    } catch {
      // Not a valid JSON-RPC response, let other handlers process it
    }
    return false;
  }

  private async request<T>(method: string, params: any = {}): Promise<T> {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      throw new Error('WebRTC DataChannel is not open');
    }

    // Wait for buffer to drain before sending (flow control)
    await this.waitForBufferDrain();

    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      throw new Error('WebRTC DataChannel closed while waiting');
    }

    const id = this.nextId++;
    const payload: JsonRpcRequest = {
      jsonrpc: '2.0',
      method,
      params,
      id
    };

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject });

      // Longer timeout for file operations and searches (30s)
      const timeoutMs = (method === 'fs.writeFile' || method === 'fs.readFile' ||
                         method === 'fs.globalSearch' || method === 'fs.getFolderSize' ||
                         method === 'fs.getFolderSizes') ? 30000 : 10000;
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error(`Request ${method} timed out`));
        }
      }, timeoutMs);

      this.dataChannel!.send(JSON.stringify(payload));
    });
  }

  // --- IFileSystem Implementation ---

  async readdir(path: string): Promise<FileStat[]> {
    if (!this.isConnected) return [];
    try {
      const res = await this.request<{ files: FileStat[] }>('fs.listDir', { path });
      return res?.files ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Stream directory contents in batches for large directories
   * @param path Directory path
   * @param onBatch Callback for each batch of files (called multiple times)
   * @returns Promise that resolves when streaming is complete
   */
  readdirStream(
    path: string,
    onBatch: (files: FileStat[], total: number, offset: number, hasMore: boolean) => void
  ): Promise<void> {
    if (!this.isConnected) {
      return Promise.reject(new Error('DataChannel not connected'));
    }

    return new Promise((resolve, reject) => {
      const id = this.nextId++;
      const payload = {
        jsonrpc: '2.0' as const,
        method: 'fs.listDir',
        params: { path, stream: true },
        id
      };

      // Register streaming handler
      this.streamingRequests.set(id, {
        onBatch,
        onComplete: resolve,
        onError: reject
      });

      // Timeout for streaming (60 seconds for very large directories)
      const timeout = setTimeout(() => {
        if (this.streamingRequests.has(id)) {
          this.streamingRequests.delete(id);
          reject(new Error('Stream request timed out'));
        }
      }, 60000);

      // Clear timeout when complete
      const originalComplete = this.streamingRequests.get(id)!.onComplete;
      this.streamingRequests.get(id)!.onComplete = () => {
        clearTimeout(timeout);
        originalComplete();
      };

      this.dataChannel!.send(JSON.stringify(payload));
    });
  }

  async getDrives(): Promise<FileStat[]> {
    if (!this.isConnected) return [];
    try {
      const res = await this.request<{ drives: FileStat[] }>('fs.getDrives');
      return res?.drives ?? [];
    } catch {
      return [];
    }
  }

  async getQuickAccess(): Promise<FileStat[]> {
    if (!this.isConnected) return [];
    try {
      const res = await this.request<{ items: FileStat[] }>('sys.getQuickAccess');
      return res?.items ?? [];
    } catch {
      return [];
    }
  }

  async getIcon(path: string, size: number): Promise<FileIconResult | null> {
    try {
      const res = await this.request<FileIconResult>('sys.getIcon', { path, size });
      return res ?? null;
    } catch {
      return null;
    }
  }

  async mkdir(path: string): Promise<void> {
    await this.request('fs.mkdir', { path });
  }

  async rename(oldPath: string, newPath: string): Promise<void> {
    await this.request('fs.rename', { oldPath, newPath });
  }

  async rm(path: string, recursive: boolean = false): Promise<void> {
    await this.request('fs.remove', { path, recursive });
  }

  async copy(sourcePath: string, destPath: string): Promise<void> {
    await this.request('fs.copy', { src: sourcePath, dest: destPath, overwrite: false });
  }

  async move(sourcePath: string, destPath: string): Promise<void> {
    // move is effectively rename in many FS, or copy+delete
    await this.request('fs.rename', { oldPath: sourcePath, newPath: destPath });
  }

  async unzip(path: string, dest: string): Promise<void> {
    await this.request('sys.unzip', { path, dest });
  }

  async readFile(path: string, options?: ReadFileOptions): Promise<ReadFileResult> {
    const CHUNK_SIZE = 9 * 1024; // Must be multiple of 3 to safely concat base64 across chunks
    const MAX_CONCURRENCY = 4;   // Reduce in-flight requests to avoid DC backpressure

    // Helper to request a single chunk
    const readChunk = async (offset: number, length: number): Promise<ReadFileResult> => {
      const res = await this.request<ReadFileResult>('fs.readFile', {
        path,
        offset,
        length,
      });
      return {
        data: res?.data ?? '',
        bytesRead: res?.bytesRead ?? 0,
        isEOF: res?.isEOF ?? true,
      };
    };

    // Case 1: Length is known (Optimized Parallel Fetch)
    if (options?.length && options.length > 0) {
      const totalBytes = options.length;
      const chunkCount = Math.ceil(totalBytes / CHUNK_SIZE);
      const chunks = new Array<string>(chunkCount);
      const bytesReadList = new Array<number>(chunkCount);
      let isEOFFinal = false;

      // Queue of tasks
      const tasks: (() => Promise<void>)[] = [];

      for (let i = 0; i < chunkCount; i++) {
        tasks.push(async () => {
          const offset = (options.offset ?? 0) + (i * CHUNK_SIZE);
          const remaining = totalBytes - (i * CHUNK_SIZE);
          const length = Math.min(CHUNK_SIZE, remaining);

          const res = await readChunk(offset, length);
          chunks[i] = res.data;
          bytesReadList[i] = res.bytesRead;
          
          // We can't easily sum bytesRead safely in parallel without locks, 
          // but since we pre-calculated lengths, we mostly care about data.
          // However, for correct EOF reporting, we should check the last chunk.
          if (i === chunkCount - 1) {
             isEOFFinal = res.isEOF;
          }
        });
      }

      // Execute with concurrency limit
      const activePromises: Promise<void>[] = [];
      for (const task of tasks) {
        if (activePromises.length >= MAX_CONCURRENCY) {
          await Promise.race(activePromises);
        }
        const p = task().then(() => {
          activePromises.splice(activePromises.indexOf(p), 1);
        });
        activePromises.push(p);
      }
      await Promise.all(activePromises);

      // Join data
      const combinedData = chunks.join('');
      const totalBytesRead = bytesReadList.reduce((sum, val) => sum + (val || 0), 0);

      return {
        data: combinedData,
        bytesRead: totalBytesRead,
        isEOF: isEOFFinal, // Rough approximation
      };
    }

    // Case 2: Length unknown (Sequential with Chunking)
    // We can't easily parallelize because we don't know the offsets until we read.
    // (Technically we could speculate, but sequential is safer for "read until end")
    
    let currentOffset = options?.offset ?? 0;
    let combinedData = '';
    let totalBytesRead = 0;
    let isEOF = false;

    while (true) {
      const res = await readChunk(currentOffset, CHUNK_SIZE);
      
      combinedData += res.data;
      totalBytesRead += res.bytesRead;
      currentOffset += res.bytesRead;
      isEOF = res.isEOF;

      if (res.isEOF || res.bytesRead === 0) {
        break;
      }
    }

    return {
      data: combinedData,
      bytesRead: totalBytesRead,
      isEOF: isEOF,
    };
  }

  async writeFile(path: string, data: string, offset: number = 0, create: boolean = true, truncate: boolean = false): Promise<number> {
    const res = await this.request<{ bytesWritten: number }>('fs.writeFile', { path, data, offset, create, truncate });
    return res.bytesWritten;
  }

  /**
   * Write binary data with pipelining (multiple chunks in flight)
   * This is more efficient than writeFile for large files
   */
  async writeFileBinary(path: string, data: ArrayBuffer, offset: number, create: boolean, truncate: boolean): Promise<number> {
    // Convert to base64 for now (backend still uses base64)
    // TODO: Implement true binary transfer protocol
    const bytes = new Uint8Array(data);
    let binary = '';
    // Use chunk processing to avoid call stack issues with large arrays
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      for (let j = 0; j < chunk.length; j++) {
        binary += String.fromCharCode(chunk[j]);
      }
    }
    const base64 = btoa(binary);
    return this.writeFile(path, base64, offset, create, truncate);
  }

  async search(options: SearchOptions): Promise<FileStat[]> {
    const res = await this.request<{ matches: FileStat[] }>('fs.search', options);
    return res.matches;
  }

  async pinToQuickAccess(path: string): Promise<void> {
    await this.request('sys.pinToQuickAccess', { path });
  }

  async unpinFromQuickAccess(path: string): Promise<void> {
    await this.request('sys.unpinFromQuickAccess', { path });
  }

  async resolvePath(path: string): Promise<string> {
    // In real system, the backend handles case-insensitivity.
    // We could either return path as-is or ask backend for the canonical name.
    try {
      const res = await this.request<{ canonicalPath: string }>('fs.resolvePath', { path });
      return res.canonicalPath;
    } catch {
      return path;
    }
  }

  /**
   * Probe the remote foreground window's directory.
   * Explorer -> { ok:true, path }, Desktop -> { ok:true, path },
   * virtual location / non-Explorer / failure -> { ok:false }
   */
  async getForegroundExplorerPath(): Promise<{ ok: boolean; path?: string }> {
    if (!this.isConnected) return { ok: false };
    try {
      const res = await this.request<{ ok: boolean; path?: string }>('sys.getForegroundExplorerPath');
      return res ?? { ok: false };
    } catch {
      return { ok: false };
    }
  }

  /**
   * Open a path on the remote desktop via the shell.
   * mode='select' -> explorer /select, (open containing folder & highlight item)
   * mode='open'   -> default program (file) or open folder (directory)
   */
  async shellOpen(path: string, mode: 'open' | 'select' = 'open'): Promise<{ ok: boolean; message?: string }> {
    if (!this.isConnected) return { ok: false, message: 'fs-rpc not connected' };
    try {
      const res = await this.request<{ ok: boolean; message?: string }>('sys.shellOpen', { path, mode });
      return res ?? { ok: false };
    } catch (e: any) {
      return { ok: false, message: e?.message };
    }
  }

  /**
   * Global file search using NTFS MFT-level indexing
   * @param query Search query string
   * @param mode 0=prefix (faster), 1=contains (slower but more thorough)
   * @param limit Maximum number of results
   * @param returnPath Whether to include full path in results
   */
  async globalSearch(query: string, mode: 0 | 1 = 0, limit: number = 200, returnPath: boolean = true): Promise<{
    results: Array<{
      volume: string;
      frn: number;
      name: string;
      is_dir: boolean;
      file_size: number;
      allocated_size: number;
      full_path: string;
    }>;
    count: number;
    status?: string;
    message?: string;
  }> {
    if (!this.isConnected) {
      throw new Error('DataChannel not connected');
    }
    const res = await this.request<{
      results: Array<{
        volume: string;
        frn: number;
        name: string;
        is_dir: boolean;
        file_size: number;
        allocated_size: number;
        full_path: string;
      }>;
      count: number;
      status?: string;
      message?: string;
    }>('fs.globalSearch', { query, mode, limit, return_path: returnPath });

    // Debug log (only log count, not full results to avoid performance issues)
    // console.log('[GlobalSearch] Query:', query, 'Mode:', mode === 0 ? 'prefix' : 'contains', 'Count:', res?.count);

    return res;
  }

  /**
   * Get folder size (recursive calculation)
   * @param path Folder path
   * @param frn File Reference Number (preferred for performance)
   * @param volume Volume name (optional, derived from path)
   */
  async getFolderSize(path: string, frn?: number, volume?: string): Promise<FolderSizeResult | null> {
    if (!this.isConnected) {
      return null;
    }
    try {
      const res = await this.request<FolderSizeResult>('fs.getFolderSize', {
        path,
        frn: frn || 0,
        volume: volume || path.slice(0, 2)
      });
      return res;
    } catch {
      return null;
    }
  }

  /**
   * Batch get folder sizes
   * @param folders Array of folder info with path and optional FRN
   */
  async getFolderSizes(folders: Array<{ path: string; frn?: number; volume?: string }>): Promise<Record<string, FolderSizeResult>> {
    if (!this.isConnected || folders.length === 0) {
      return {};
    }
    try {
      const res = await this.request<{ results: Record<string, FolderSizeResult> }>('fs.getFolderSizes', {
        folders: folders.map(f => ({
          path: f.path,
          frn: f.frn || 0,
          volume: f.volume || f.path.slice(0, 2)
        }))
      });
      return res?.results || {};
    } catch {
      return {};
    }
  }

  /**
   * One-shot top-N children sizes for the disk-space treemap.
   * The host runs a single SQLite pass (folder_stats JOIN) and returns the
   * largest children plus an aggregated "others", so a directory with thousands
   * of subfolders costs ONE request instead of an RPC storm.
   * @param path Parent folder path
   * @param limit Max children to return (host defaults to 80)
   * @param frn Parent File Reference Number (0 = let host resolve once)
   * @param volume Volume name (optional, derived from path)
   */
  async getChildrenSizes(path: string, limit = 80, frn = 0, volume?: string): Promise<ChildrenSizesResult | null> {
    if (!this.isConnected) {
      return null;
    }
    try {
      const res = await this.request<ChildrenSizesResult>('fs.getChildrenSizes', {
        path,
        limit,
        frn: frn || 0,
        volume: volume || path.slice(0, 2)
      });
      return res || null;
    } catch {
      return null;
    }
  }

  /**
   * Get volume (disk) size
   * @param volume Volume name (e.g. "C:" or "C:\\")
   */
  async getVolumeSize(volume: string): Promise<{ total_size: number; free_size: number; used_size: number } | null> {
    if (!this.isConnected) {
      return null;
    }
    try {
      return await this.request<{ total_size: number; free_size: number; used_size: number }>('fs.getVolumeSize', { volume });
    } catch {
      return null;
    }
  }

  // ===== File Search Engine Management =====

  /**
   * Drive information for file search engine initialization
   */
  async getAvailableDrivesForInit(): Promise<DriveInfo[]> {
    if (!this.isConnected) {
      return [];
    }
    try {
      const res = await this.request<{ drives: DriveInfo[] }>('fs.getAvailableDrivesForInit');
      return res?.drives || [];
    } catch (e) {
      console.error('[WebRTCSystem] getAvailableDrivesForInit failed:', e);
      return [];
    }
  }

  /**
   * Initialize file search engine with selected volumes
   * @param volumes Array of volume letters to index, e.g., ["C:", "D:"]
   */
  async initSearchEngine(volumes: string[]): Promise<{ success: boolean; message: string }> {
    if (!this.isConnected) {
      throw new Error('DataChannel not connected');
    }
    const res = await this.request<{ success: boolean; message: string }>('fs.initSearchEngine', { volumes });
    if (res.success) {
      this.startStatusPolling();
    }
    return res;
  }

  /**
   * Reinitialize file search engine with new volumes (shuts down existing engine first)
   * @param volumes Array of volume letters to index, e.g., ["C:", "D:"]
   */
  async reinitSearchEngine(volumes: string[]): Promise<{ success: boolean; message: string }> {
    if (!this.isConnected) {
      throw new Error('DataChannel not connected');
    }
    const res = await this.request<{ success: boolean; message: string }>('fs.reinitSearchEngine', { volumes });
    if (res.success) {
      this.startStatusPolling();
    }
    return res;
  }

  /**
   * Force every per-volume persistence worker to flush pending events to disk.
   * timeout_ms defaults to 5000.
   */
  async flushPersist(timeout_ms: number = 5000): Promise<{ success: boolean; rc: number }> {
    if (!this.isConnected) {
      throw new Error('DataChannel not connected');
    }
    return this.request<{ success: boolean; rc: number }>('fs.flushPersist', { timeout_ms });
  }

  /**
   * Delete every per-volume index.db on disk.
   * @param rebuild  true (default) → host 立即触发全扫；false → 仅删文件，由调用方接着
   *                 reinitSearchEngine(volumes) 触发扫描（避免双扫，常用于"清理 + 重新选盘"流程）
   * 返回: { deleted, rebuilt, rebuild_rc }
   */
  async clearPersist(rebuild: boolean = true): Promise<{ deleted: number; rebuilt: boolean; rebuild_rc: number }> {
    if (!this.isConnected) {
      throw new Error('DataChannel not connected');
    }
    const res = await this.request<{ deleted: number; rebuilt: boolean; rebuild_rc: number }>('fs.clearPersist', { rebuild });
    // 只有触发了 rebuild 才需要立刻重启状态轮询；否则交给后续 reinit 自己启
    if (rebuild) this.startStatusPolling();
    return res;
  }

  /**
   * Get init timing report (last Start() pass). Used by frontend "本次初始化耗时" panel.
   * 字段为 snake_case 与后端 InitTimingReport 一一对应.
   */
  async getInitTiming(): Promise<InitTimingReport> {
    if (!this.isConnected) {
      throw new Error('DataChannel not connected');
    }
    return this.request<InitTimingReport>('fs.initTiming', {});
  }

  /**
   * Get per-volume persistence status (file_count, last_persist_ms_ago, dirty, etc.).
   */
  async persistStats(): Promise<{
    volumes: Array<{
      vol_index: number;
      letter?: string;
      enabled: boolean;
      reason?: string;
      file_count?: number;
      dir_count?: number;
      last_usn?: number;
      last_persist_ms_ago?: number;
      dirty?: boolean;
      worker_running?: boolean;
    }>;
    error?: string;
  }> {
    if (!this.isConnected) {
      throw new Error('DataChannel not connected');
    }
    return this.request('fs.persistStats', {});
  }

  /**
   * Get current search engine status from backend
   * Call this on page load to sync frontend state with backend
   */
  async getSearchEngineStatus(): Promise<SearchEngineStatusResponse> {
    if (!this.isConnected) {
      return { status: 'not_started', error: '', ready: false };
    }
    try {
      const res = await this.request<SearchEngineStatusResponse>('fs.globalSearchStatus');
      return res;
    } catch (e) {
      console.error('[WebRTCSystem] getSearchEngineStatus failed:', e);
      return { status: 'not_started', error: '', ready: false };
    }
  }

  /**
   * Start polling search engine status
   * Polls every 1.5s until status becomes 'ready' or 'error'.
   * Safe to call multiple times (stops existing polling first).
   */
  startStatusPolling(): void {
    // Stop any existing polling
    this.stopStatusPolling();


    // Set initial status to indexing
    useFileSearchStatusStore.getState().updateStatus({
      status: FileSearchStatus.INDEXING,
      message: '正在初始化...',
    });

    const poll = async () => {
      if (!this.isConnected) {
        this.stopStatusPolling();
        return;
      }

      try {
        const res = await this.getSearchEngineStatus();

        // Map backend status to frontend status
        let frontendStatus: FileSearchStatus;
        switch (res.status) {
          case 'ready':
            frontendStatus = FileSearchStatus.READY;
            break;
          case 'initializing':
          case 'indexing':
            frontendStatus = FileSearchStatus.INDEXING;
            break;
          case 'building_index':
            frontendStatus = FileSearchStatus.BUILDING_INDEX;
            break;
          case 'calculating_sizes':
            frontendStatus = FileSearchStatus.CALCULATING_SIZES;
            break;
          case 'error':
          case 'not_started':
          default:
            frontendStatus = FileSearchStatus.UNINITIALIZED;
            break;
        }

        // Update store with full progress info
        useFileSearchStatusStore.getState().updateStatus({
          status: frontendStatus,
          progress: res.progress,
          current: res.current,
          total: res.total,
          phase: res.phase,
          message: res.message || res.error || '',
        });

        // Stop polling if status is terminal
        if (res.status === 'ready' || res.status === 'error') {
          this.stopStatusPolling();
        }
      } catch (e) {
        console.error('[WebRTCSystem] Status polling error:', e);
        // Don't stop polling on error, keep trying
      }
    };

    // Delay first poll to allow initial INDEXING status to render in UI
    // Without this delay, poll() completes before React renders, and user sees no progress
    setTimeout(poll, 300);
    this.statusPollingTimer = setInterval(poll, 1500);
  }

  /**
   * Stop polling search engine status
   */
  private stopStatusPolling(): void {
    if (this.statusPollingTimer) {
      clearInterval(this.statusPollingTimer);
      this.statusPollingTimer = null;
    }
  }
}

/**
 * Search engine status response from backend
 */
export interface SearchEngineStatusResponse {
  status: string;  // 'not_started' | 'initializing' | 'ready' | 'error'
  error?: string;
  ready: boolean;
  progress?: number;  // 0-100 percentage
  current?: number;   // Current count
  total?: number;     // Total count
  phase?: string;     // Phase description
  message?: string;   // Additional message
  volumes?: string[]; // Volumes that were initialized (e.g., ["C:", "D:"])
}

/**
 * Init timing report (last Start() pass) returned by fs.initTiming RPC.
 * 字段命名 snake_case 与后端 InitTimingReport (file_search_engine.h) 一一对应.
 */
export interface InitTimingPerVolume {
  letter: string;            // "C"
  source: 'loaded' | 'full_scan' | 'persist_disabled' | 'smb_share' | string;
  load_ms: number;
  replay_ms: number;
  replay_count: number;
  enumerate_ms: number;
  bulk_persist_ms: number;
  file_count: number;
  dir_count: number;
}

export interface InitTimingReport {
  valid: boolean;
  success: boolean;
  error?: string;

  total_ms: number;
  start_unix_ms: number;
  end_unix_ms: number;

  init_volumes_ms: number;
  open_persistence_ms: number;
  load_persistence_ms: number;
  replay_usn_ms: number;
  enumerate_volumes_ms: number;
  prefix_index_ms: number;
  contains_index_ms: number;   // -1 表示 async, 未计入主路径
  folder_stats_ms: number;     // 0 表示 skip
  bulk_persist_ms: number;
  start_workers_ms: number;
  start_usn_monitors_ms: number;

  volume_count_total: number;
  volume_count_loaded_from_persist: number;
  volume_count_full_scan: number;
  volume_count_persist_disabled: number;

  strategy: 'all_loaded' | 'all_full_scan' | 'mixed' | 'none' | string;
  per_volume: InitTimingPerVolume[];
}

/**
 * Drive information for frontend display
 */
export interface DriveInfo {
  volume: string;       // e.g., "C:"
  label: string;        // Volume label
  file_system: string;  // e.g., "NTFS"
  total_size: number;   // Total size in bytes
  free_size: number;    // Free size in bytes
  drive_type: string;   // "fixed", "removable", "network", etc.
  is_ntfs: boolean;     // Whether it's NTFS
}
